import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';
import { formatScorePercentage, showsScoreAndPercent } from '@/lib/assessment-score-display';
import { withLibraryContext } from '@/lib/library-navigation';
import { assessmentSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface AssessmentForm {
  title: string;
  slug: string;
  questionBankId: string;
}

interface QuestionBank {
  id: string;
  title: string;
  questionsCount?: number;
}

interface AssessmentSettings {
  type?: string;
  max_questions?: number;
  min_questions_to_pass?: number;
  require_passing_score?: boolean;
  show_score_and_percent?: boolean;
  time_limit_minutes?: number;
  unlimited_attempts?: boolean;
  max_attempts?: number | null;
  randomize_questions?: boolean;
  show_answers?: boolean;
  auto_grade?: boolean;
}

interface AssessmentDetail {
  id: string;
  title: string;
  slug: string;
  status?: string;
  questionBank?: { id: string; title: string } | null;
  currentVersion?: { settings?: AssessmentSettings | null } | null;
}

type AssessmentStatus = 'draft' | 'published';

const STATUS_LABELS: Record<AssessmentStatus, string> = {
  draft: 'Borrador',
  published: 'Publicada',
};

const ASSESSMENT_TYPE = 'exam' as const;

function buildSettings(
  maxQuestions: number,
  minToPass: number,
  requirePassingScore: boolean,
  showScoreAndPercent: boolean,
  timeLimit: number,
  unlimitedAttempts: boolean,
  maxAttempts: number,
  randomize: boolean,
  showAnswers: boolean,
  autoGrade: boolean,
) {
  return {
    type: ASSESSMENT_TYPE,
    max_questions: maxQuestions,
    min_questions_to_pass: minToPass,
    require_passing_score: requirePassingScore,
    show_score_and_percent: showScoreAndPercent,
    time_limit_minutes: timeLimit,
    unlimited_attempts: unlimitedAttempts,
    max_attempts: unlimitedAttempts ? null : maxAttempts,
    randomize_questions: randomize,
    show_answers: showAnswers,
    auto_grade: autoGrade,
  };
}

export function CreateAssessmentPage() {
  const { id: editId } = useParams<{ id: string }>();
  const isEdit = Boolean(editId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [maxQuestions, setMaxQuestions] = useState(10);
  const [minToPass, setMinToPass] = useState(6);
  const [requirePassingScore, setRequirePassingScore] = useState(true);
  const [showScoreAndPercent, setShowScoreAndPercent] = useState(true);
  const [timeLimit, setTimeLimit] = useState(30);
  const [unlimitedAttempts, setUnlimitedAttempts] = useState(true);
  const [maxAttempts, setMaxAttempts] = useState(3);
  const [randomize, setRandomize] = useState(true);
  const [showAnswers, setShowAnswers] = useState(false);
  const [autoGrade, setAutoGrade] = useState(true);
  const [status, setStatus] = useState<AssessmentStatus>('draft');

  const { data: banksData } = useQuery({
    queryKey: ['question-banks'],
    queryFn: () => api<{ data: QuestionBank[] }>('/questions/banks'),
  });

  const banks = banksData?.data ?? [];

  const { data: existing, isLoading: loadingExisting } = useQuery({
    queryKey: ['assessment', editId],
    queryFn: () => api<AssessmentDetail>(`/assessments/${editId}`),
    enabled: isEdit,
  });

  const { register, handleSubmit, watch, reset } = useForm<AssessmentForm>();
  const bankId = watch('questionBankId') ?? '';

  useEffect(() => {
    if (!existing) return;
    const settings = (existing.currentVersion?.settings ?? {}) as AssessmentSettings;
    reset({
      title: existing.title,
      slug: existing.slug,
      questionBankId: existing.questionBank?.id ?? '',
    });
    if (typeof settings.max_questions === 'number') setMaxQuestions(settings.max_questions);
    if (typeof settings.min_questions_to_pass === 'number') setMinToPass(settings.min_questions_to_pass);
    if (typeof settings.require_passing_score === 'boolean') setRequirePassingScore(settings.require_passing_score);
    setShowScoreAndPercent(showsScoreAndPercent(settings));
    if (typeof settings.time_limit_minutes === 'number') setTimeLimit(settings.time_limit_minutes);
    if (typeof settings.unlimited_attempts === 'boolean') setUnlimitedAttempts(settings.unlimited_attempts);
    if (typeof settings.max_attempts === 'number') setMaxAttempts(settings.max_attempts);
    if (typeof settings.randomize_questions === 'boolean') setRandomize(settings.randomize_questions);
    if (typeof settings.show_answers === 'boolean') setShowAnswers(settings.show_answers);
    if (typeof settings.auto_grade === 'boolean') setAutoGrade(settings.auto_grade);
    if (existing.status === 'published' || existing.status === 'draft') setStatus(existing.status);
  }, [existing, reset]);

  const available = useMemo(() => {
    return banks.find((b) => b.id === bankId)?.questionsCount ?? 0;
  }, [bankId, banks]);

  const timeLabel = timeLimit > 0 ? `${timeLimit} min` : 'Sin límite';
  const minimumPercent =
    requirePassingScore && maxQuestions > 0
      ? formatScorePercentage(minToPass, maxQuestions)
      : null;

  const mutation = useMutation({
    mutationFn: (values: AssessmentForm) => {
      const settings = buildSettings(
        maxQuestions,
        minToPass,
        requirePassingScore,
        showScoreAndPercent,
        timeLimit,
        unlimitedAttempts,
        maxAttempts,
        randomize,
        showAnswers,
        autoGrade,
      );
      const body = {
        title: values.title,
        slug: values.slug,
        status,
        questionBankId: values.questionBankId || undefined,
        settings,
      };

      if (isEdit && editId) {
        return api<{ id: string }>(`/assessments/${editId}`, { method: 'PATCH', body });
      }

      return api<{ id: string }>('/assessments', { method: 'POST', body });
    },
    onSuccess: (result: { id: string }) => {
      toast(assessmentSuccessToast(isEdit ? 'edit' : 'create'));
      void queryClient.invalidateQueries({ queryKey: ['assessments'] });
      void queryClient.invalidateQueries({ queryKey: ['assessment', result.id] });
      navigate(withLibraryContext(`/evaluaciones/${result.id}`, searchParams));
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, isEdit ? 'No se pudo guardar la evaluación' : 'No se pudo crear la evaluación'));
    },
  });

  if (isEdit && loadingExisting) {
    return (
      <div className="lms-assessment-create lms-site-admin-hub-page font-sans">
        <div className="flex justify-center py-24">
          <Loader />
        </div>
      </div>
    );
  }

  if (isEdit && !loadingExisting && !existing) {
    return (
      <div className="lms-assessment-create lms-site-admin-hub-page font-sans">
        <p className="text-sm text-red-600" role="alert">
          Evaluación no encontrada
        </p>
        <Link to="/evaluaciones" className="mt-4 inline-flex text-sm text-primary">
          Volver al catálogo
        </Link>
      </div>
    );
  }

  const backUrl =
    isEdit && editId ? withLibraryContext(`/evaluaciones/${editId}`, searchParams) : '/evaluaciones';
  const backLabel = isEdit ? 'Volver al detalle de la evaluación' : 'Volver a Biblioteca de Evaluaciones';

  return (
    <div className="lms-assessment-create lms-site-admin-hub-page font-sans">
      <header className="mb-6 flex items-start gap-4">
        <HubBackLink to={backUrl} label={backLabel} />
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
            {isEdit ? 'Editar evaluación' : 'Nueva evaluación'}
          </h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            {isEdit ? 'Actualiza la configuración de la evaluación' : 'Crea una evaluación reutilizable en la biblioteca'}
          </p>
        </div>
      </header>

      <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="lms-assessment-create__form">
        <div className="lms-assessment-create__main">
          <section className="lms-assessment-create__card">
            <h2 className="lms-assessment-create__card-title">Información General</h2>

            <div className="lms-assessment-create__grid-2">
              <div className="lms-assessment-create__field">
                <label htmlFor="title">
                  Nombre <span className="lms-assessment-create__required">*</span>
                </label>
                <input
                  id="title"
                  type="text"
                  required
                  placeholder="Ej. Prueba Parcial 1"
                  {...register('title', { required: true })}
                />
              </div>
              <div className="lms-assessment-create__field">
                <label htmlFor="code">
                  Código <span className="lms-assessment-create__optional">(Opcional)</span>
                </label>
                <input id="code" type="text" name="code" placeholder="Ej. EVAL-101" disabled />
              </div>
            </div>

            <div className="lms-assessment-create__grid-2">
              <div className="lms-assessment-create__field">
                <label htmlFor="slug">Slug</label>
                <input id="slug" type="text" {...register('slug', { required: true })} />
              </div>
              <div className="lms-assessment-create__field">
                <label htmlFor="assessment_status">Estado</label>
                <div className="lms-assessment-create__select-wrap">
                  <select
                    id="assessment_status"
                    value={status}
                    onChange={(event) => setStatus(event.target.value === 'published' ? 'published' : 'draft')}
                  >
                    <option value="draft">Borrador</option>
                    <option value="published">Publicada</option>
                  </select>
                  <span className="material-symbols-outlined" aria-hidden="true">
                    expand_more
                  </span>
                </div>
                <p className="lms-assessment-create__hint">
                  {status === 'published'
                    ? 'Queda disponible para usarla en cursos.'
                    : 'Sigue en preparación y no se ofrece a los alumnos.'}
                </p>
              </div>
            </div>

            <div className="lms-assessment-create__field">
              <label htmlFor="description">Descripción</label>
              <textarea
                id="description"
                name="description"
                rows={3}
                placeholder="Instrucciones u objetivos de la evaluación..."
                disabled
              />
            </div>

            <div className="lms-assessment-create__field">
              <label htmlFor="tags_input">Etiquetas</label>
              <input id="tags_input" type="text" name="tags_input" placeholder="seguridad, induccion" disabled />
              <p className="lms-assessment-create__hint">Separa las etiquetas con comas.</p>
            </div>
          </section>

          <section className="lms-assessment-create__card">
            <h2 className="lms-assessment-create__card-title">Estructura y Puntuación</h2>

            <div className="lms-assessment-create__field">
                <label htmlFor="question_bank_id">Banco de Preguntas</label>
                <div className="lms-assessment-create__select-wrap">
                  <select id="question_bank_id" {...register('questionBankId')}>
                    <option value="">Selecciona un banco</option>
                    {banks.map((bank) => (
                      <option key={bank.id} value={bank.id}>
                        {bank.title}
                        {bank.questionsCount != null ? ` (${bank.questionsCount} preguntas)` : ''}
                      </option>
                    ))}
                  </select>
                  <span className="material-symbols-outlined" aria-hidden="true">
                    expand_more
                  </span>
                </div>
                {bankId && (
                  <p className="lms-assessment-create__hint">{available} preguntas disponibles en el banco</p>
                )}
            </div>

            <div className="lms-assessment-create__grid-3">
              <div className="lms-assessment-create__field">
                <label htmlFor="max_questions">Cantidad de preguntas</label>
                <input
                  id="max_questions"
                  type="number"
                  min={1}
                  value={maxQuestions}
                  onChange={(e) => setMaxQuestions(Number(e.target.value))}
                />
              </div>
              <div className="lms-assessment-create__field">
                <label htmlFor="min_questions_to_pass">Mínimo para aprobar</label>
                <input
                  id="min_questions_to_pass"
                  type="number"
                  min={0}
                  value={minToPass}
                  disabled={!requirePassingScore}
                  onChange={(e) => setMinToPass(Number(e.target.value))}
                />
                {minimumPercent && (
                  <p className="lms-assessment-create__hint">
                    {minToPass} / {maxQuestions} · {minimumPercent}
                  </p>
                )}
              </div>
              <div className="lms-assessment-create__field">
                <label htmlFor="time_limit_minutes">Tiempo límite (min)</label>
                <input
                  id="time_limit_minutes"
                  type="number"
                  min={0}
                  value={timeLimit}
                  onChange={(e) => setTimeLimit(Number(e.target.value))}
                />
              </div>
            </div>

            <div className="lms-assessment-create__toggle-row">
              <div>
                <h3>Exigir mínimo para aprobar</h3>
                {!requirePassingScore && (
                  <p>Si se desactiva, enviar la evaluación cuenta como aprobado sin importar el puntaje.</p>
                )}
              </div>
              <label className="lms-assessment-create__switch">
                <input
                  type="checkbox"
                  checked={requirePassingScore}
                  onChange={(event) => setRequirePassingScore(event.target.checked)}
                />
                <span />
              </label>
            </div>

            <div className="lms-assessment-create__toggle-row">
              <div>
                <h3>Mostrar calificación y %</h3>
                <p>
                  {showScoreAndPercent
                    ? 'El estudiante ve la nota y el porcentaje al terminar y en sus intentos.'
                    : 'La nota y el porcentaje quedan ocultos. Solo se muestra si aprobó o reprobó.'}
                </p>
              </div>
              <label className="lms-assessment-create__switch">
                <input
                  type="checkbox"
                  checked={showScoreAndPercent}
                  onChange={(event) => setShowScoreAndPercent(event.target.checked)}
                />
                <span />
              </label>
            </div>

            {!requirePassingScore && (
              <div className="lms-assessment-create__info">
                <span className="material-symbols-outlined" aria-hidden="true">
                  info
                </span>
                <p>
                  Cada pregunta vale <strong>1 punto</strong>. La calificación máxima será igual a la cantidad de
                  preguntas establecidas.
                </p>
              </div>
            )}
          </section>

          <section className="lms-assessment-create__card">
            <h2 className="lms-assessment-create__card-title">Opciones de Ejecución</h2>

            <div className="lms-assessment-create__toggles">
              <div className="lms-assessment-create__toggle-row">
                <div>
                  <h3>Intentos ilimitados</h3>
                  <p>Permite al estudiante repetir la evaluación múltiples veces.</p>
                </div>
                <label className="lms-assessment-create__switch">
                  <input
                    type="checkbox"
                    checked={unlimitedAttempts}
                    onChange={(e) => setUnlimitedAttempts(e.target.checked)}
                  />
                  <span />
                </label>
              </div>

              {!unlimitedAttempts && (
                <div className="lms-assessment-create__field lms-assessment-create__field--nested">
                  <label htmlFor="max_attempts">Número de intentos</label>
                  <input
                    id="max_attempts"
                    type="number"
                    min={1}
                    value={maxAttempts}
                    onChange={(e) => setMaxAttempts(Math.max(1, Number.parseInt(e.target.value, 10) || 1))}
                  />
                </div>
              )}

              <div className="lms-assessment-create__toggle-row">
                <div>
                  <h3>Orden aleatorio</h3>
                  <p>Mezcla el orden de las preguntas para cada intento.</p>
                </div>
                <label className="lms-assessment-create__switch">
                  <input type="checkbox" checked={randomize} onChange={(e) => setRandomize(e.target.checked)} />
                  <span />
                </label>
              </div>

              <div className="lms-assessment-create__toggle-row">
                <div>
                  <h3>Mostrar respuestas</h3>
                  <p>Muestra las respuestas correctas al finalizar el intento.</p>
                </div>
                <label className="lms-assessment-create__switch">
                  <input type="checkbox" checked={showAnswers} onChange={(e) => setShowAnswers(e.target.checked)} />
                  <span />
                </label>
              </div>

              <div className="lms-assessment-create__field">
                <label htmlFor="correction_mode">Modo de corrección</label>
                <div className="lms-assessment-create__select-wrap">
                  <select
                    id="correction_mode"
                    value={autoGrade ? 'automatic' : 'manual'}
                    onChange={(event) => setAutoGrade(event.target.value === 'automatic')}
                  >
                    <option value="automatic">Automático</option>
                    <option value="manual">Manual</option>
                  </select>
                  <span className="material-symbols-outlined" aria-hidden="true">
                    expand_more
                  </span>
                </div>
                <p className="lms-assessment-create__hint">
                  {autoGrade
                    ? 'La nota se calcula al enviar. Las preguntas que exigen revisión siguen pendientes.'
                    : 'El intento queda en revisión. Un docente marca cada respuesta antes de publicar la nota.'}
                </p>
              </div>
            </div>
          </section>
        </div>

        <aside className="lms-assessment-create__aside">
          <div className="lms-assessment-create__summary">
            <h3>Resumen de Configuración</h3>

            <div className="lms-assessment-create__summary-rows">
              <div>
                <span>Tipo</span>
                <strong className="lms-assessment-create__pill">Examen</strong>
              </div>
              <div>
                <span>Estado</span>
                <strong>{STATUS_LABELS[status]}</strong>
              </div>
              <div>
                <span>Preguntas</span>
                <strong>{maxQuestions}</strong>
              </div>
              <div>
                <span>Pts. para Aprobar</span>
                <strong>
                  {requirePassingScore
                    ? `${minToPass} / ${maxQuestions}${minimumPercent ? ` · ${minimumPercent}` : ''}`
                    : 'No exigido'}
                </strong>
              </div>
              <div>
                <span>Calificación y %</span>
                <strong>{showScoreAndPercent ? 'Visible' : 'Oculta'}</strong>
              </div>
              <div>
                <span>Tiempo</span>
                <strong>{timeLabel}</strong>
              </div>
            </div>

            <div className="lms-assessment-create__summary-flags">
              {randomize && (
                <div>
                  <span className="material-symbols-outlined" aria-hidden="true">
                    check_circle
                  </span>
                  Orden aleatorio activo
                </div>
              )}
              <div>
                <span className="material-symbols-outlined" aria-hidden="true">
                  check_circle
                </span>
                {autoGrade ? 'Corrección automática' : 'Corrección manual'}
              </div>
              {unlimitedAttempts ? (
                <div>
                  <span className="material-symbols-outlined" aria-hidden="true">
                    check_circle
                  </span>
                  Intentos ilimitados
                </div>
              ) : (
                <div>
                  <span className="material-symbols-outlined" aria-hidden="true">
                    check_circle
                  </span>
                  {maxAttempts} {maxAttempts === 1 ? 'intento' : 'intentos'}
                </div>
              )}
              {showAnswers && (
                <div>
                  <span className="material-symbols-outlined" aria-hidden="true">
                    check_circle
                  </span>
                  Mostrar respuestas activo
                </div>
              )}
              {showScoreAndPercent && (
                <div>
                  <span className="material-symbols-outlined" aria-hidden="true">
                    check_circle
                  </span>
                  Calificación y % visibles
                </div>
              )}
            </div>

            <div className="lms-assessment-create__summary-actions">
              <button type="submit" className="lms-assessment-create__submit" disabled={mutation.isPending}>
                {isEdit ? 'Guardar cambios' : 'Crear evaluación'}
              </button>
              <Link to={backUrl} className="lms-assessment-create__cancel">
                Cancelar
              </Link>
            </div>
          </div>
        </aside>
      </form>
    </div>
  );
}
